import { v4 as uuidv4 } from 'uuid';
const _ = require('lodash');

import BlockComponent from './BlockComponent';

/**
 * Page对象，结构如下(兼容xmind格式)
 * {
 *    "id": "<uuid>"
      "title": "page1",
      "note": {},
      "topics": [
        {
          "title": "component",
          "note": {},   // 此处为component-schema
          "topics": []  // 此处为slot内容
        }
      ]
    }
 */
export default class Page {
  /**
   * component为设计云中的component表中的数据条目
   * @param {*} component 
   */
  constructor(component) {
    component = component || {};
    this.key = component.key || uuidv4()
    this.component = component
    this.title = ''
    this.note = {
      style: {
        css: ''
      },
      data: {
        '数据交互名称': {
          'name': '数据交互名称', 
          'model': '',
          'api': 'create/read/update/delete/',
          'callback': {
            'type': 'data | code',
            'value': '其他数据交互或者代码'
          },
          'params': [{
            'key': 'colum1',
            'operation': '=',
            'value': 'abc'
          }]
        }
      }
    }
    this.blockComponents = []
  }

  /**
   * 返回所有的blockComponent，展开成一个数组
   */
  getAllBlockComponents(parent) {
    let self = this;
    let blockComponents = [];

    if (!parent) {
      parent = this;
    }

    _.each(parent.blockComponents, blockComponent => {
      blockComponents.push(Object.assign(new BlockComponent(), {
        key: blockComponent.key,
        component: blockComponent.component,
        title: blockComponent.title,
        note: blockComponent.note,
        blockComponents: []
      }))

      blockComponents = _.concat(blockComponents, self.getAllBlockComponents(blockComponent))
    })

    return blockComponents;
  }

  /**
   * 在页面中新增component
   * @param {*} afterComponentKey 在某个component后插入component. key为 utils/component.js中的key
   * @param {*} blockComponent 插入的BlockComponent的对象（component的结构为mysql中的组件表数据条目）
   * @param {*} parentComponentKey 父元素的key
   */
  addComponent(afterComponentKey, blockComponent, parentComponentKey) {
    if (parentComponentKey && !afterComponentKey) {
      this._addChildComponent(this, parentComponentKey, blockComponent);
      return;
    }

    if (!afterComponentKey) {
      this.blockComponents.unshift(blockComponent)
      return;
    }

    this._addComponentRecursively(this.blockComponents, afterComponentKey, blockComponent)
  }

  /**
   * 在父component内添加component
   * @param searchComponent
   * @param {*} parentComponentKey 
   * @param {*} blockComponent 
   */
  _addChildComponent(searchComponent, parentComponentKey, blockComponent) {
    let self = this;
    if (searchComponent.key === parentComponentKey) {
      searchComponent.blockComponents.push(blockComponent);
      return;
    }

    searchComponent.blockComponents.forEach(childComponent => {
      self._addChildComponent(childComponent, parentComponentKey, blockComponent);
    })
  }

  _addComponentRecursively(blockComponents, afterComponentKey, blockComponent) {
    let afterIndex = _.findIndex(blockComponents, item => item.key === afterComponentKey)
    if ( afterIndex < 0) {
      blockComponents.forEach(item => this._addComponentRecursively(item.blockComponents, afterComponentKey, blockComponent))
      return;
    }
    blockComponents.splice(afterIndex+1, 0, blockComponent)
  }

  /**
   * 移动组件
   * @param {*} componentKey 
   */
  moveComponent(componentKey, afterComponentKey, parentComponentKey) {
    let deletedComponent = this.getBlockComponent(componentKey)
    this.delComponent(componentKey)
    this.addComponent(afterComponentKey, deletedComponent, parentComponentKey)
  }

  /**
   * 在页面中删除component
   * @param {*} componentKey 
   */
  delComponent(componentKey) {
    this._delComponentRecursively(this.blockComponents, componentKey)
  }

  _delComponentRecursively(blockComponents, componentKey) {
    let componentIndex = _.findIndex(blockComponents, item => item.key === componentKey)
    if (componentIndex < 0) {
      blockComponents.forEach(item => this._delComponentRecursively(item.blockComponents, componentKey))
    }
    blockComponents.splice(componentIndex, 1)
  }

  /**
   * 获取某个组件的 BlockComponent
   * @param {*} componentKey 
   */
  getBlockComponent(componentKey) {
    return this._getBlockComponent(this, componentKey)
  }

  getChildBlockComponent(parent, componentKey) {
    if (!parent) {
      return null
    }
    
    return _.keyBy(parent.blockComponents, 'key')[componentKey]
  }

  /**
   * 递归查找componentKey
   * @param {*} parentComponent 
   * @param {*} componentKey 
   */
  _getBlockComponent(parentComponent, componentKey) {
    let ret = this.getChildBlockComponent(parentComponent, componentKey)
    if (ret) {
      return ret
    }

    for(let i = 0; i < parentComponent.blockComponents.length; i++) {
      ret = this._getBlockComponent(parentComponent.blockComponents[i], componentKey)
      if (ret) {
        return ret
      }
    }
  }

  /**
   * 获取某个组件的父组件
   * @param {*} componentKey 
   */
  getParentBlockComponent(componentKey) {
    return this._getParentBlockComponent(this, componentKey)
  }

  _getParentBlockComponent(rootComponent, componentKey) {
    if (this.getChildBlockComponent(rootComponent, componentKey)) {
      return rootComponent;
    }

    for(let i = 0; i < rootComponent.blockComponents.length; i++) {
      let _parent = this._getParentBlockComponent(rootComponent.blockComponents[i],  componentKey)
      if (_parent) {
        return _parent;
      } 
    }
    return null;
  }

  /**
   * @deprecated 获取到编辑页面的this.pageData数据
   */
  getEditingPageData() {
    return {
      key: this.key,
      title: this.title,
      note: this.note,
      component: this.component,
      topics: this.getTopicDict()
    };
  }

  getTopicDict() {
    return _.chain(this.getAllBlockComponents())
    .map(item => {
      return {
        key: item.key,
        title: item.title,
        note: item.note,
        doc: item.doc
      }
    })
    .keyBy('key')
    .value();
  }

  toJSON() {
    return {
      key: this.key,
      title: this.title,
      note: this.note,
      component: this.component,
      topics: _.map(this.blockComponents, (blockComponent) => blockComponent.toJSON()),
      topicDict: this.getTopicDict()
    }
  }

  static fromJSON(pageJson) {
    let pageComponent = _.assign(pageJson.component, {
      key: pageJson.key
    })
    let page = new Page(pageComponent);
    page.note = pageJson.note
    page.title = pageJson.title
    page.key = pageJson.key
    
    _.each(pageJson.topics, topic => {
      page.blockComponents.push(BlockComponent.fromJSON(topic))
    })
    return page;
  }
}